import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, RefreshCw, RotateCcw, Check, X, AlertCircle, Settings, Headphones, Activity, Info, Clock, CheckCircle, XCircle, Loader } from 'lucide-react';
import { helpdeskApi, HelpdeskConfig, SyncLog } from '@/lib/api/helpdesk';
import { AddIntegrationModal } from '@/components/helpdesk/AddIntegrationModal';
import { ReconnectModal } from '@/components/helpdesk/ReconnectModal';
import { CustomerAccountsTab } from '@/components/helpdesk/CustomerAccountsTab';

export function HelpdeskSettingsPage() {
  const navigate = useNavigate();
  const { configId } = useParams();
  const [configs, setConfigs] = useState<HelpdeskConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [syncingConfig, setSyncingConfig] = useState<string | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<HelpdeskConfig | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadConfigs();
  }, []);

  // Load config from URL parameter
  useEffect(() => {
    if (configId && configs.length > 0) {
      const config = configs.find(c => c.id === configId);
      if (config) {
        setSelectedConfig(config);
      }
    } else if (!configId) {
      setSelectedConfig(null);
    }
  }, [configId, configs]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const data = await helpdeskApi.listConfigs();
      setConfigs(data);
    } catch (error) {
      console.error('Failed to load helpdesk configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (configId: string, full = false) => {
    try {
      setSyncingConfig(configId);
      await helpdeskApi.syncTickets(configId, full);
      await loadConfigs(); // Reload to get updated sync status
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncingConfig(null);
    }
  };

  const handleDelete = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this helpdesk integration?')) {
      return;
    }

    try {
      await helpdeskApi.deleteConfig(configId);
      await loadConfigs();
    } catch (error) {
      console.error('Failed to delete config:', error);
    }
  };

  // If a config is selected, show details view
  if (selectedConfig) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
              {
                label: 'Helpdesk Integration',
                href: '/settings/helpdesk',
                icon: <Headphones className="w-4 h-4" />
              },
              {
                label: selectedConfig.name,
                icon: <Headphones className="w-4 h-4" />
              }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedConfig.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage helpdesk integration and ticket sync schedules
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/settings/helpdesk')}
                className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
              >
                Back to List
              </button>
              <button
                onClick={() => handleDelete(selectedConfig.id)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
              >
                <Trash2 className="w-5 h-5" />
                Delete
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', label: 'Overview', icon: Info },
                { id: 'customers', label: 'Customer accounts', icon: Headphones },
                { id: 'schedules', label: 'Schedules', icon: Clock },
                { id: 'history', label: 'Sync History', icon: Activity }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            {activeTab === 'customers' && <CustomerAccountsTab key={selectedConfig.id} config={selectedConfig} />}
            {activeTab === 'overview' && (
              <OverviewTab config={selectedConfig} onSync={handleSync} onReconnected={loadConfigs} />
            )}
            {activeTab === 'schedules' && (
              <SchedulesTab configId={selectedConfig.id} />
            )}
            {activeTab === 'history' && (
              <SyncHistoryTab configId={selectedConfig.id} />
            )}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Settings', href: '/settings', icon: <Settings className="w-4 h-4" /> },
            {
              label: 'Helpdesk Integration',
              icon: <Headphones className="w-4 h-4" />
            }
          ]}
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Helpdesk Integration
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Connect your ticketing system to monitor support tickets
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Integration
          </button>
        </div>

        {/* Configurations List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
            <p className="text-gray-600 dark:text-gray-400 mt-4">Loading configurations...</p>
          </div>
        ) : configs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700"
          >
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No helpdesk integrations configured
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Get started by adding your first helpdesk integration
            </p>
            <button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Integration
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {configs.map((config, index) => (
              <ConfigCard
                key={config.id}
                config={config}
                index={index}
                onSync={handleSync}
                onDelete={handleDelete}
                isSyncing={syncingConfig === config.id}
                onViewDetails={(id) => navigate(`/settings/helpdesk/${id}`)}
              />
            ))}
          </div>
        )}

        {/* Add Integration Modal */}
        <AnimatePresence>
          {showAddForm && (
            <AddIntegrationModal
              onClose={() => setShowAddForm(false)}
              onSuccess={() => {
                setShowAddForm(false);
                loadConfigs();
              }}
            />
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}

// Config Card Component
function ConfigCard({
  config,
  index,
  onSync,
  onDelete,
  isSyncing,
  onViewDetails,
}: {
  config: HelpdeskConfig;
  index: number;
  onSync: (id: string) => void;
  onDelete: (id: string) => void;
  isSyncing: boolean;
  onViewDetails: (id: string) => void;
}) {
  const providerColors = {
    zoho: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    servicenow: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    zendesk: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    jira: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    itsm_mcp: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  };

  const getSyncStatusBadge = () => {
    if (isSyncing) {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Syncing
        </span>
      );
    }

    if (config.sync_status === 'success') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <Check className="w-3 h-3" />
          Synced
        </span>
      );
    }

    if (config.sync_status === 'partial') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <AlertCircle className="w-3 h-3" />
          Partial
        </span>
      );
    }

    if (config.sync_status === 'failed') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <X className="w-3 h-3" />
          Failed
        </span>
      );
    }

    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer"
      onClick={() => onViewDetails(config.id)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {config.name}
            </h3>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${providerColors[config.provider]}`}>
              {config.provider === 'itsm_mcp' ? 'ITSM' : config.provider.toUpperCase()}
            </span>
            {!config.is_active && (
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                Inactive
              </span>
            )}
            {getSyncStatusBadge()}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            {config.region && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Region</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {config.region.toUpperCase()}
                </p>
              </div>
            )}
            {config.last_sync_at && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Last Sync</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {new Date(config.last_sync_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {config.sync_error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-xs text-red-700 dark:text-red-400">
                <strong>Error:</strong> {config.sync_error}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSync(config.id);
            }}
            disabled={isSyncing || !config.is_active}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Sync tickets"
          >
            <RefreshCw className={`w-5 h-5 ${isSyncing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(config.id);
            }}
            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
            title="Delete integration"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// Overview Tab Component
function OverviewTab({ config, onSync, onReconnected }: {
  config: HelpdeskConfig;
  onSync: (id: string, full?: boolean) => void;
  onReconnected: () => void;
}) {
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [linkedCustomers, setLinkedCustomers] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    helpdeskApi.getCustomerAccounts(config.id)
      .then(links => { if (active) setLinkedCustomers(new Set(links.map(link => link.customer_id)).size); })
      .catch(() => { if (active) setLinkedCustomers(null); });
    return () => { active = false; };
  }, [config.id]);

  const handleSync = async () => {
    setSyncing(true);
    await onSync(config.id);
    setSyncing(false);
  };

  // An ordinary sync reads the detail only of tickets whose status moved, so a
  // ticket stored wrongly stays wrong. A full resync is the way to repair it.
  const handleFullResync = async () => {
    if (!confirm(
      'A full resync re-reads every ticket from the helpdesk, not only the ones '
      + 'whose status changed, and can take many minutes. Continue?'
    )) {
      return;
    }
    setResyncing(true);
    await onSync(config.id, true);
    setResyncing(false);
  };

  return (
    <div className="space-y-6">
      {reconnecting && (
        <ReconnectModal
          config={config}
          onClose={() => setReconnecting(false)}
          onSuccess={() => { setReconnecting(false); onReconnected(); }}
        />
      )}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configuration Details</h3>
        <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setReconnecting(true)}
          className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
        >
          Reconnect
        </button>
        <button
          type="button"
          onClick={handleFullResync}
          disabled={syncing || resyncing || !config.is_active}
          title="Re-reads every ticket, not only the ones whose status changed. Takes many minutes."
          className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCcw className={`w-5 h-5 ${resyncing ? 'animate-spin' : ''}`} />
          {resyncing ? 'Resyncing...' : 'Full resync'}
        </button>
        <button
          onClick={handleSync}
          disabled={syncing || resyncing || !config.is_active}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncing ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-5 h-5" />
              Sync Tickets Now
            </>
          )}
        </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Provider</label>
            <p className="text-base font-medium text-gray-900 dark:text-white capitalize">
              {config.provider}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Region</label>
            <p className="text-base font-medium text-gray-900 dark:text-white uppercase">
              {config.region || 'N/A'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Linked customers</label>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              {linkedCustomers === null ? '—' : linkedCustomers}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              {config.is_active ? (
                <span className="text-green-600 dark:text-green-400">Active</span>
              ) : (
                <span className="text-gray-400">Inactive</span>
              )}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Last Sync</label>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              {config.last_sync_at ? new Date(config.last_sync_at).toLocaleString() : 'Never'}
            </p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Sync Status</label>
            <p className="text-base font-medium text-gray-900 dark:text-white">
              {config.sync_status === 'success' && (
                <span className="text-green-600 dark:text-green-400">Success</span>
              )}
              {config.sync_status === 'partial' && (
                <span className="text-amber-600 dark:text-amber-400">
                  Partial &mdash; the listing stopped early and some tickets were not refreshed
                </span>
              )}
              {config.sync_status === 'failed' && (
                <span className="text-red-600 dark:text-red-400">Failed</span>
              )}
              {!config.sync_status && <span className="text-gray-400">N/A</span>}
            </p>
          </div>
        </div>
      </div>

      {config.sync_error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">
            <strong>Last Error:</strong> {config.sync_error}
          </p>
        </div>
      )}
    </div>
  );
}

// Schedules Tab Component
function SchedulesTab({ configId }: { configId: string }) {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    enabled: true,
    interval_minutes: 60,
    sync_type: 'incremental',
    fetch_limit: 100
  });

  useEffect(() => {
    loadSchedules();
  }, [configId]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const data = await helpdeskApi.listSchedules(configId);
      setSchedules(data);
    } catch (error) {
      console.error('Failed to load schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      name: '',
      description: '',
      enabled: true,
      interval_minutes: 60,
      sync_type: 'incremental',
      fetch_limit: 100
    });
    setShowAddModal(true);
  };

  const handleEdit = (schedule: any) => {
    setSelectedSchedule(schedule);
    setFormData({
      name: schedule.name,
      description: schedule.description || '',
      enabled: schedule.enabled,
      interval_minutes: schedule.interval_minutes || 60,
      sync_type: schedule.sync_type,
      fetch_limit: schedule.fetch_limit
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (schedule: any) => {
    setSelectedSchedule(schedule);
    setShowDeleteModal(true);
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await helpdeskApi.createSchedule(configId, {
        config_id: configId,
        ...formData
      });
      setShowAddModal(false);
      await loadSchedules();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      alert('Failed to create schedule');
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSchedule) return;

    try {
      await helpdeskApi.updateSchedule(selectedSchedule.id, formData);
      setShowEditModal(false);
      await loadSchedules();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      alert('Failed to update schedule');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule) return;

    try {
      await helpdeskApi.deleteSchedule(selectedSchedule.id);
      setShowDeleteModal(false);
      await loadSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('Failed to delete schedule');
    }
  };

  const handleToggle = async (scheduleId: string) => {
    try {
      await helpdeskApi.toggleSchedule(scheduleId);
      await loadSchedules();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading schedules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ticket Sync Schedules</h3>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Schedule
        </button>
      </div>

      {schedules.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="font-medium">No schedules configured</p>
          <p className="text-sm mt-2">Create a schedule to automate ticket synchronization</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="font-medium text-gray-900 dark:text-white">{schedule.name}</h4>
                    <button
                      onClick={() => handleToggle(schedule.id)}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        schedule.enabled
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                      {schedule.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  {schedule.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{schedule.description}</p>
                  )}
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                    <span>Interval: {schedule.interval_minutes} minutes</span>
                    <span>Type: {schedule.sync_type}</span>
                    <span>Limit: {schedule.fetch_limit} tickets</span>
                    {schedule.next_run_at && (
                      <span>Next run: {new Date(schedule.next_run_at).toLocaleString()}</span>
                    )}
                  </div>
                  {schedule.last_error && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      Last error: {schedule.last_error}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(schedule)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(schedule)}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Schedule Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Schedule</h3>
              <form onSubmit={handleSubmitAdd} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name*</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Interval (minutes)*</label>
                  <input
                    type="number"
                    required
                    min="5"
                    value={formData.interval_minutes}
                    onChange={(e) => setFormData({ ...formData, interval_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sync Type</label>
                  <select
                    value={formData.sync_type}
                    onChange={(e) => setFormData({ ...formData, sync_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="incremental">Incremental</option>
                    <option value="full">Full</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fetch Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.fetch_limit}
                    onChange={(e) => setFormData({ ...formData, fetch_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="enabled" className="text-sm text-gray-700 dark:text-gray-300">Enabled</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Create
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Schedule Modal - Same as Add but with different handler */}
      <AnimatePresence>
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Schedule</h3>
              <form onSubmit={handleSubmitEdit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name*</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Interval (minutes)*</label>
                  <input
                    type="number"
                    required
                    min="5"
                    value={formData.interval_minutes}
                    onChange={(e) => setFormData({ ...formData, interval_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sync Type</label>
                  <select
                    value={formData.sync_type}
                    onChange={(e) => setFormData({ ...formData, sync_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white">
                    <option value="incremental">Incremental</option>
                    <option value="full">Full</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fetch Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.fetch_limit}
                    onChange={(e) => setFormData({ ...formData, fetch_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enabled-edit"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="enabled-edit" className="text-sm text-gray-700 dark:text-gray-300">Enabled</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Update
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Schedule</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete <strong>{selectedSchedule?.name}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Sync History Tab Component
function SyncHistoryTab({ configId }: { configId: string }) {
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSyncLogs();
  }, [configId]);

  const loadSyncLogs = async () => {
    try {
      setLoading(true);
      const logs = await helpdeskApi.getSyncLogs(configId, 0, 50);
      setSyncLogs(logs);
    } catch (error) {
      console.error('Failed to load sync logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />;
      case 'in_progress':
        return <Loader className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading sync history...</span>
      </div>
    );
  }

  if (syncLogs.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <Activity className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="font-medium">No sync history yet</p>
        <p className="text-sm mt-2">Sync logs will appear here after your first ticket synchronization</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sync Execution History</h3>
        <button
          onClick={loadSyncLogs}
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        {syncLogs.map((log) => (
          <motion.div
            key={log.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-0.5">{getStatusIcon(log.status)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {log.sync_type} Sync
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(log.started_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Fetched:</span>
                      <span className="ml-1 font-medium text-gray-900 dark:text-white">
                        {log.tickets_fetched}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>
                      <span className="ml-1 font-medium text-green-600 dark:text-green-400">
                        {log.tickets_created}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Updated:</span>
                      <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">
                        {log.tickets_updated}
                      </span>
                    </div>
                  </div>

                  {log.error_message && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      {log.error_message}
                    </div>
                  )}
                </div>
              </div>

              <div className="text-right ml-4">
                <div className="text-xs text-gray-500 dark:text-gray-400">Duration</div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDuration(log.duration_seconds)}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default HelpdeskSettingsPage;
